// Icon factory for creating Leaflet marker icons

import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { generateColoredIconUrl } from '../utils';
import { SELECTED_MARKER_ICON_URL } from '../constants';

/**
 * Fix Leaflet default icon paths for bundlers
 */
export function fixLeafletIconPaths(): void {
  // @ts-expect-error - Fixing Leaflet default icon paths
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
  });
}

/**
 * Common icon configuration
 */
const ICON_CONFIG = {
  iconSize: [25, 41] as [number, number],
  iconAnchor: [12, 41] as [number, number],
  popupAnchor: [1, -34] as [number, number],
  shadowSize: [41, 41] as [number, number],
  shadowUrl: markerShadow,
};

/**
 * Factory for creating marker icons
 */
export class IconFactory {
  private iconCache: Map<string, L.Icon> = new Map();
  private defaultIcon: L.Icon.Default;
  private selectedIcon: L.Icon;

  constructor() {
    this.defaultIcon = new L.Icon.Default();
    this.selectedIcon = L.icon({
      iconUrl: SELECTED_MARKER_ICON_URL,
      ...ICON_CONFIG,
    });
  }

  /**
   * Get icon for a selected marker
   */
  getSelectedIcon(): L.Icon {
    return this.selectedIcon;
  }

  /**
   * Get default marker icon
   */
  getDefaultIcon(): L.Icon.Default {
    return this.defaultIcon;
  }

  /**
   * Get or create colored icon
   * @param color - Hex color code
   * @returns Leaflet icon
   */
  getColoredIcon(color: string): L.Icon {
    if (this.iconCache.has(color)) {
      return this.iconCache.get(color)!;
    }

    const icon = L.icon({
      iconUrl: generateColoredIconUrl(color),
      ...ICON_CONFIG,
    });

    this.iconCache.set(color, icon);
    return icon;
  }

  /**
   * Clear the icon cache
   */
  clearCache(): void {
    this.iconCache.clear();
  }

  /**
   * Check if an icon is the selected icon
   * @param icon - Icon to check
   */
  isSelectedIcon(icon: L.Icon): boolean {
    return icon === this.selectedIcon;
  }

  /**
   * Check if an icon is the default icon
   * @param icon - Icon to check
   */
  isDefaultIcon(icon: L.Icon): boolean {
    return icon instanceof L.Icon.Default;
  }
}
